import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { UploadCloud, CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";
import { QUOTE_PRODUCTS, waLink } from "../data/site";
import { track } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-4 focus:ring-brand/10";

const Label = ({ children, htmlFor }) => (
    <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-smoke">
        {children}
    </label>
);

export const QuoteForm = ({ defaultProduct = "" }) => {
    const [form, setForm] = useState({
        nome: "",
        empresa: "",
        whatsapp: "",
        produto: defaultProduct,
        quantidade: "",
        prazo: "",
        descricao: "",
    });
    const [file, setFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (f) {
            setFile(f);
            track("file_upload", { name: f.name });
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSending(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => data.append(k, v));
            if (file) data.append("arquivo", file);
            const res = await fetch(`${API}/quotes`, { method: "POST", body: data });
            if (!res.ok) throw new Error("request failed");
            const json = await res.json();
            track("form_submit", { produto: form.produto });
            setResult(json);
        } catch (err) {
            setError("Não foi possível enviar agora. Tente novamente ou chame direto no WhatsApp.");
        } finally {
            setSending(false);
        }
    };

    if (result) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                data-testid="quote-form-success"
                className="rounded-2xl border border-brand/25 bg-brand-tint p-8 sm:p-10 text-center"
            >
                <CheckCircle2 size={44} className="mx-auto text-brand" />
                <h3 className="mt-5 font-display text-2xl font-extrabold text-ink">Recebemos sua solicitação.</h3>
                <p className="mx-auto mt-3 max-w-md text-sm sm:text-base text-smoke">
                    Em breve entraremos em contato para entender seu projeto. Para agilizar, continue a conversa agora
                    mesmo no WhatsApp — seus dados já vão preenchidos.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                        href={result.whatsapp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("whatsapp_click", { location: "form_success" })}
                        data-testid="quote-success-whatsapp"
                        className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-dark"
                    >
                        Continuar no WhatsApp
                        <ArrowUpRight size={16} />
                    </a>
                    <button
                        onClick={() => {
                            setResult(null);
                            setFile(null);
                            setForm({ nome: "", empresa: "", whatsapp: "", produto: defaultProduct, quantidade: "", prazo: "", descricao: "" });
                        }}
                        data-testid="quote-form-reset"
                        className="text-sm font-semibold text-smoke underline-offset-4 hover:text-ink hover:underline"
                    >
                        Enviar outra solicitação
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
                <Label htmlFor="qf-nome">Nome *</Label>
                <input id="qf-nome" data-testid="quote-input-nome" required value={form.nome} onChange={set("nome")} placeholder="Seu nome" className={inputCls} />
            </div>
            <div>
                <Label htmlFor="qf-empresa">Empresa</Label>
                <input id="qf-empresa" data-testid="quote-input-empresa" value={form.empresa} onChange={set("empresa")} placeholder="Nome da empresa" className={inputCls} />
            </div>
            <div>
                <Label htmlFor="qf-whats">WhatsApp *</Label>
                <input id="qf-whats" data-testid="quote-input-whatsapp" required type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder="(19) 99999-9999" className={inputCls} />
            </div>
            <div>
                <Label htmlFor="qf-produto">Qual produto/serviço? *</Label>
                <select id="qf-produto" data-testid="quote-select-produto" required value={form.produto} onChange={set("produto")} className={inputCls}>
                    <option value="" disabled>Selecione</option>
                    {QUOTE_PRODUCTS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
            </div>
            <div>
                <Label htmlFor="qf-qtd">Quantidade</Label>
                <input id="qf-qtd" data-testid="quote-input-quantidade" value={form.quantidade} onChange={set("quantidade")} placeholder="Ex.: 500 unidades" className={inputCls} />
            </div>
            <div>
                <Label htmlFor="qf-prazo">Prazo desejado</Label>
                <input id="qf-prazo" data-testid="quote-input-prazo" value={form.prazo} onChange={set("prazo")} placeholder="Ex.: até dia 20" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
                <Label htmlFor="qf-desc">Descrição do projeto</Label>
                <textarea id="qf-desc" data-testid="quote-input-descricao" rows={4} value={form.descricao} onChange={set("descricao")} placeholder="Conte o que você precisa: medidas, cores, referências..." className={`${inputCls} resize-none`} />
            </div>
            <div className="sm:col-span-2">
                <input ref={fileRef} type="file" id="qf-file" data-testid="quote-input-file" onChange={onFile} className="hidden" accept=".pdf,.ai,.cdr,.png,.jpg,.jpeg,.svg,.zip" />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="quote-upload-button"
                    className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#E5E5E5] bg-white px-4 py-6 text-sm text-smoke transition-colors hover:border-brand/50 hover:text-ink"
                >
                    <UploadCloud size={20} className="text-brand" />
                    {file ? <span className="font-semibold text-ink">{file.name}</span> : "Anexe sua arte ou referência (PDF, AI, CDR, PNG...)"}
                </button>
            </div>

            <AnimatePresence>
                {error && (
                    <motion.p
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        data-testid="quote-form-error"
                        className="sm:col-span-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    >
                        {error}{" "}
                        <a href={waLink()} target="_blank" rel="noopener noreferrer" className="underline">Abrir WhatsApp</a>
                    </motion.p>
                )}
            </AnimatePresence>

            <div className="sm:col-span-2 flex flex-col sm:flex-row items-center gap-4 pt-2">
                <button
                    type="submit"
                    disabled={sending}
                    data-testid="quote-form-submit-button"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand px-9 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-brand-dark disabled:opacity-60"
                >
                    {sending ? <Loader2 size={17} className="animate-spin" /> : null}
                    {sending ? "Enviando..." : "Solicitar orçamento"}
                </button>
                <p className="text-xs text-smoke">Resposta rápida em horário comercial. Seus dados ficam com a IMPRIMA AKI.</p>
            </div>
        </form>
    );
};
